import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { bookingRepository } from "./repositories/booking.repository";
import { bookingCancellationRepository } from "./repositories/booking-cancellation.repository";
import { Booking, BookingCancellationEntity } from "./types/booking.entity";
import { callFunctionSafe } from "src/firebase/functions";

export type BookingsFilter = Partial<
  Pick<
    Booking,
    "coachId" | "learnerId" | "bookingStatus" | "coachType" | "slotId" | "langCode"
  >
> & {
  bookingId?: string;
};

const bookingsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getBookings: builder.query<(Booking & { id: string })[], { where?: BookingsFilter; offset?: string}>({
      queryFn: async ({ where = {}, offset }) => {
        // Direct booking id lookup
        if (where.bookingId) {
          const { bookingId, ...rest } = where;
          const res = await executeQuery(() => bookingRepository.findById(bookingId!));
          if ("error" in res) return { error: res.error } as any;
          const booking = res.data as (Booking & { id: string }) | null;
          if (!booking) return { data: [] } as any;
          // Match remaining filters client-side if provided
          const matches = Object.entries(rest).every(([k, v]) => (v == null ? true : (booking as any)[k] === v));
          return { data: matches ? [booking] : [] } as any;
        }

        const result = await executeQuery(() => bookingRepository.find({ where, offset, orderBy: ["createdAt", "desc"] } as any));
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (Booking & { id: string })[] };
      },
      providesTags: ["Booking"],
    }),

    getBookingsBySlotId: builder.query<(Booking & { id: string })[], string>({
      queryFn: async (slotId) => {
        const result = await executeQuery(() =>
          bookingRepository.find({ where: { slotId }, limit: 100, orderBy: ["createdAt", "desc"] } as any)
        );
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (Booking & { id: string })[] } as any;
      },
      providesTags: ["Booking"],
    }),

    countBookings: builder.query<number, { where?: BookingsFilter }>({
      queryFn: async ({ where = {} }) => {
        if (where.bookingId) {
          const { bookingId, ...rest } = where;
          const res = await executeQuery(() => bookingRepository.findById(bookingId!));
          if ("error" in res) return { error: res.error } as any;
          const booking = res.data as Booking | null;
          if (!booking) return { data: 0 } as any;
          const matches = Object.entries(rest).every(([k, v]) => (v == null ? true : (booking as any)[k] === v));
          return { data: matches ? 1 : 0 } as any;
        }
        return executeQuery(() => bookingRepository.count(where as any)) as any;
      },
      providesTags: ["Booking"],
    }),

    getBookingById: builder.query<Booking & { id: string }, string>({
      queryFn: async (id) => {
        const result = await executeQuery(() => bookingRepository.findById(id));
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as Booking & { id: string } };
      },
      providesTags: ["Booking"],
    }),

    getBookingCancellationByBookingId: builder.query<(BookingCancellationEntity & { id: string }) | null, string>({
      queryFn: async (bookingId) => {
        const result = await executeQuery(() => bookingCancellationRepository.findById(bookingId));
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (BookingCancellationEntity & { id: string }) | null };
      },
      providesTags: ["Booking"],
    }),

    createBookingsById: builder.mutation<
      { bookingsStatus: "CONFIRMED" | "WAITING_FOR_APPROVE"; bookingIds: string[] },
      { userId: string; slotsIds: string[]; note?: string }
    >({
      queryFn: async (payload) => {
        return callFunctionSafe("createBookings", payload) as any;
      },
      invalidatesTags: ["Booking"],
    }),
  }),
});

export const {
  useGetBookingsQuery,
  useGetBookingsBySlotIdQuery,
  useCountBookingsQuery,
  useGetBookingByIdQuery,
  useGetBookingCancellationByBookingIdQuery,
  useCreateBookingsByIdMutation,
} = bookingsService;


